<!--
 * @Description: 验证码登录、密码登录
 * @Date: 2021-11-10 13:44:01
-->
<template>
  <base-form class="user-login-form" ref="formRef" :model="form" :rules="rules">
    <template v-if="itemList.includes(FormItemEnum.phone)">
      <base-form-item :prop="FormItemEnum.phone">
        <base-input
          style="width: 100%"
          :inputStyle="{ height: '40px' }"
          :maxlength="11"
          clearable
          v-model="form[FormItemEnum.phone]"
          placeholder="请输入手机号"
          @input="onInputNumber($event, FormItemEnum.phone)"
        />
      </base-form-item>
      <base-form-item :prop="FormItemEnum.code">
        <div class="user-login-form-code">
          <base-input
            class="user-login-form-inputcode"
            :inputStyle="{ height: '40px' }"
            clearable
            :maxlength="4"
            v-model="form[FormItemEnum.code]"
            placeholder="请输入验证码"
            @input="onInputNumber($event, FormItemEnum.code)"
          />
          <XrkSlideVerify
            ref="verificationRef"
            :options="verificationOptions"
            @success="checkSuccessful"
          >
            <base-button
              :click="onGetcode"
              :disable="Boolean(countdown)"
              :class="[
                'user-login-form-getcode',
                countdown ? 'user-login-form-diabled-btn' : ''
              ]"
            >
              {{ countdown || codeMsg }}
            </base-button>
          </XrkSlideVerify>
        </div>
      </base-form-item>
    </template>

    <template v-if="itemList.includes(FormItemEnum.account)">
      <base-form-item :prop="FormItemEnum.account">
        <base-input
          style="width: 100%"
          type="text"
          placeholder="请输入账号"
          clearable
          inputTextReplace
          :inputTexts="['英文字母', '数字', '中文']"
          :maxlength="16"
          :inputStyle="{ height: '40px' }"
          v-model="form[FormItemEnum.account]"
        />
      </base-form-item>
    </template>

    <template v-if="itemList.includes(FormItemEnum.pwd)">
      <base-form-item :prop="FormItemEnum.pwd">
        <base-input
          type="password"
          placeholder="请输入密码"
          style="width: 100%"
          show-password
          :inputStyle="{ height: '40px' }"
          :maxlength="8"
          v-model="form[FormItemEnum.pwd]"
        />
      </base-form-item>
    </template>

    <template v-if="itemList.includes(FormItemEnum.pwd2)">
      <base-form-item :prop="FormItemEnum.pwd2">
        <base-input
          type="password"
          placeholder="请再次输入密码"
          style="width: 100%"
          show-password
          :inputStyle="{ height: '40px' }"
          :maxlength="8"
          v-model="form[FormItemEnum.pwd2]"
        />
      </base-form-item>
    </template>

    <template v-if="itemList.includes(FormItemEnum.agreement)">
      <base-form-item
        :prop="FormItemEnum.agreement"
        style="margin-bottom: 28px; height: 26px"
      >
        <div class="user-login-form-agreement">
          <BaseCheckbox
            v-model="form[FormItemEnum.agreement]"
            :trueValue="AgreementEnum.TRUE"
            :falseValue="AgreementEnum.FALSE"
          ></BaseCheckbox>
          <p style="color: #1a1a1a; flex-shrink: 0">我同意遵守</p>
          <div style="color: #fa8203; cursor: pointer">
            <div @click="onLookAgreement(1)">
              《{{ PLATFORM_NAME }}服务平台用户协议》
            </div>
            <div
              v-if="isRegister"
              @click="onLookAgreement(2)"
              style="margin-top: -10px"
            >
              《隐私政策》
            </div>
          </div>
        </div>
      </base-form-item>
    </template>
  </base-form>
</template>

<script lang="ts" setup>
import { ref, withDefaults, defineProps, defineExpose, watch } from 'vue';
import {
  BaseForm,
  BaseFormItem,
  BaseInput,
  BaseFormRef,
  BaseButton,
  BaseFormProps,
  BaseCheckbox,
  XrkSlideVerify,
  XrkMessageBox
} from 'xrk-components';

import type { FormData, FormProps } from '../../types/index';
import { validatorPassword, validatorPhone } from '@/utils/validator';
import {
  FormItemEnum,
  AgreementEnum,
  LoginTypeEnum
} from '../../constants/index';
import { imageJson, lastProtocol, lastPolicy } from '@data-source/common/apis';
import { sendMessageCode } from '../../data-source/apis';
import { formatter } from '@/utils';
import { PLATFORM_NAME } from '@/config';

const AGREEMENT_MAPPING = {
  1: {
    api: lastProtocol,
    title: `${PLATFORM_NAME}服务平台用户协议`
  },
  2: {
    api: lastPolicy,
    title: '隐私政策'
  }
};

const props = withDefaults(defineProps<FormProps>(), {
  formData: () => {
    return {};
  },
  pageType: ''
});

const isRegister = ref(props.pageType === LoginTypeEnum.REGISTER);

const codeMsg = ref('获取验证码');
const formRef = ref<BaseFormRef>(null);
const form = ref<FormData>({
  [FormItemEnum.phone]: '',
  [FormItemEnum.code]: '',
  [FormItemEnum.account]: '',
  [FormItemEnum.pwd]: '',
  [FormItemEnum.pwd2]: '',
  [FormItemEnum.agreement]: AgreementEnum.FALSE
});

const countdown = ref<number>(0);
const verificationRef = ref();
const verificationOptions = ref({
  remoteTimerInterval: 800,
  errorText: '请控制拼图块对齐缺口',
  successText: '验证成功',
  remoteApi: {
    initImages: () => {
      return imageJson({
        [FormItemEnum.phone]: form.value[FormItemEnum.phone]
      });
    },
    checkVerification: position => {
      return sendMessageCode({
        [FormItemEnum.phone]: form.value[FormItemEnum.phone],
        ...position
      });
    }
  },
  errorAnimationDuration: 800
});

const rules = ref<BaseFormProps['rules']>({
  [FormItemEnum.phone]: [
    {
      required: true,
      trigger: 'blur',
      validator: validatorPhone
    }
  ],
  [FormItemEnum.code]: [
    {
      required: true,
      trigger: 'blur',
      validator: (_, value, callback) => {
        const regex = /^[0-9]{4}$/;
        if (!value || !regex.test(value)) {
          return callback(new Error('请输入正确验证码'));
        } else {
          callback();
        }
      }
    }
  ],
  [FormItemEnum.account]: [
    { required: true, trigger: 'blur', message: '请您输入正确的账号' }
  ],
  [FormItemEnum.pwd]: [
    { required: true, trigger: 'blur', validator: validatorPassword }
  ],

  [FormItemEnum.pwd2]: [
    {
      required: true,
      trigger: 'blur',
      validator: (_rule, value, callback) => {
        if (!value) {
          callback(new Error('请再次输入密码'));
        } else if (form.value[FormItemEnum.pwd] !== value) {
          callback(new Error('两次密码输入不一致'));
        } else {
          callback();
        }
      }
    }
  ],
  [FormItemEnum.agreement]: [
    {
      required: true,
      trigger: 'blur',
      validator: (_rule, value, callback) => {
        if (!value) {
          if (isRegister.value) {
            return callback(
              new Error(
                `请先阅读并勾选${PLATFORM_NAME}服务平台用户协议&隐私政策`
              )
            );
          }
          return callback(
            new Error(`请先阅读并勾选${PLATFORM_NAME}服务平台用户协议`)
          );
        } else {
          callback();
        }
      }
    }
  ]
});

watch(
  () => props?.formData,
  formData => {
    if (formData) {
      form.value = { ...form.value, ...formData };
    }
  },
  { deep: true, immediate: true }
);

const onInputNumber = (value, prop) => {
  if (value) {
    form.value[prop] = value.replace(/[^\d]/g, '');
  } else {
    form.value[prop] = '';
  }
};

const onGetcode = async () => {
  if (countdown.value) return;
  await formRef.value.validateField(FormItemEnum.phone);
  verificationRef.value.show();
};

const onLookAgreement = async (type: 1 | 2) => {
  const {
    data: { content, releaseAt, takeEffectAt }
  } = await AGREEMENT_MAPPING[type].api();
  XrkMessageBox.alert(
    `<div style="max-height: 500px;overflow-y: auto"><div style="margin-bottom:15px"><h1>更新日期：${formatter.date(
      releaseAt,
      'YYYY-MM-DD'
    )}</h1><h1>生效日期：${formatter.date(
      takeEffectAt,
      'YYYY-MM-DD'
    )}</h1></div> <div>${content}</div></div>`,
    AGREEMENT_MAPPING[type].title,
    {
      customStyle: { width: '800px', maxWidth: '800px' },
      dangerouslyUseHTMLString: true,
      confirmButtonText: '同意',
      beforeClose: (action, instance, done) => {
        if (action === 'confirm') {
          form.value[FormItemEnum.agreement] = AgreementEnum.TRUE;
        }
        done();
      }
    }
  );
};

const checkSuccessful = () => {
  if (countdown.value === 0) {
    countdown.value = 60;
    const intervalId = setInterval(() => {
      if (countdown.value === 1) codeMsg.value = '重新获取验证码';

      if (countdown.value > 0) {
        countdown.value--;
      } else {
        clearInterval(intervalId);
      }
    }, 1000);
  }
};
defineExpose({
  validate: (...args: any) => formRef.value?.validate(...args),
  resetFields: (...args: any) => formRef.value?.resetFields(...args),
  data: form
});
</script>

<style lang="scss" scoped>
.user-login-form {
  .base-input {
    border-radius: 2px 2px 2px 2px;
    height: 40px;
  }
  .user-login-form-code {
    width: 100%;
    display: flex;
    align-items: center;
    .user-login-form-inputcode {
      flex: 1;
    }
    .user-login-form-getcode {
      width: 114px;
      height: 40px;
      color: #ffffff;
      margin-left: 17px;
    }
    .user-login-form-diabled-btn {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  .user-login-form-agreement {
    display: flex;
    font-size: 14px;
    color: #1a1a1a;
    box-sizing: border-box;
  }
}
</style>
